import React from 'react'

import {
  CockpitDemo,
  JournalActionsDemo,
  JournalCardDemo,
  JournalHeaderDemo,
  MenuPlanCardDemo,
  SettingsSwitchDemo,
  SidebarWidgetsDemo,
  TourWrapper
} from './ui-demos'

import { GaugeGoal, PageLayout } from 'Components'

interface HeadlineAnchorProps {
  id: string
  offset?: string
}

const HeadlineAnchor: React.FC<HeadlineAnchorProps> = ({ id, offset = '-80px' }) => (
  <a id={id} style={{ display: 'block', position: 'relative', top: offset }} />
)

const TableOfContentsEntry: React.FC = ({ children }) => (
  <li style={{ textDecoration: 'underline', marginBottom: '0.3rem' }}>{children}</li>
)

const HelpAndTourPage: React.FC<{ isHelpPage: boolean }> = ({ isHelpPage }) => {
  return (
    <PageLayout variant="smallcircle">
      <div className="container max-700 pb-6">
        <section className="content-section">
          <h1 className="pb-4">{isHelpPage ? 'Hilfe' : 'eBalance-Tour'}</h1>
          <ul>
            <TableOfContentsEntry>
              <a href="#tagebuch">Das eBalance-Tagebuch</a>
            </TableOfContentsEntry>
            <TableOfContentsEntry>
              <a className="ml-3" href="#cockpit">
                Cockpit und Übersicht
              </a>
            </TableOfContentsEntry>
            <TableOfContentsEntry>
              <a className="ml-3" href="#eintragen">
                Einträge hinzufügen
              </a>
            </TableOfContentsEntry>
            <TableOfContentsEntry>
              <a className="ml-3" href="#seitenleiste">
                Seitenleiste im Tagebuch
              </a>
            </TableOfContentsEntry>
            <TableOfContentsEntry>
              <a href="#menueplan">Ihr individueller Menüplan</a>
            </TableOfContentsEntry>
            <TableOfContentsEntry>
              <a href="#analyse">Die eBalance-Analyse</a>
            </TableOfContentsEntry>
            <TableOfContentsEntry>
              <a className="ml-3" href="#naehrwerte">
                Nährwertdiagramm
              </a>
            </TableOfContentsEntry>
            <TableOfContentsEntry>
              <a className="ml-3" href="#kalorienbilanz">
                Kalorienbilanz
              </a>
            </TableOfContentsEntry>
            <TableOfContentsEntry>
              <a className="ml-3" href="#mahlzeitenverteilung">
                Mahlzeitenverteilung
              </a>
            </TableOfContentsEntry>
            <TableOfContentsEntry>
              <a className="ml-3" href="#masse">
                Masse
              </a>
            </TableOfContentsEntry>
            <TableOfContentsEntry>
              <a href="#inspirieren">Lassen Sie sich inspirieren</a>
            </TableOfContentsEntry>
            <TableOfContentsEntry>
              <a href="#eigene">Eigene Nahrungsmittel und Rezepte erstellen</a>
            </TableOfContentsEntry>
            <TableOfContentsEntry>
              <a href="#einstellungen">Einstellungen und persönliche Daten</a>
            </TableOfContentsEntry>
            <TableOfContentsEntry>
              <a href="#faq">Häufige Fragen (FAQ)</a>
            </TableOfContentsEntry>
            <TableOfContentsEntry>
              <a href="#feedback">Feedback</a>
            </TableOfContentsEntry>
          </ul>
        </section>

        <section className="content-section">
          <h2>
            <HeadlineAnchor id="tagebuch" />
            Das eBalance-Tagebuch
          </h2>
          <p>
            Das eBalance-Tagebuch ist das Herzstück unseres Abnehmprogramms. Unser Algorithmus berechnet Ihre
            individuelle Kalorienbilanz auf Basis Ihrer zu sich genommenen Nahrungsmittel und ausgeführten Aktivitäten.
          </p>
          <p>
            Das Tagebuch ist in zwei Spalten aufgebaut: Links finden Sie Ihr Cockpit, die Einträge und Mahlzeiten,
            rechts optionale Widgets wie Makronährstoffe, Flüssigkeitszufuhr, Gemüse und Früchte sowie Notizen. Über die
            Icons in der Kopfzeile gelangen Sie schnell zu <em>Rezepte</em>, <em>Mein Essen</em>, <em>Favoriten</em>,
            der <em>Nährwertanalyse</em> und den <em>Einstellungen</em>.
          </p>

          <h3>
            <HeadlineAnchor id="cockpit" />
            Cockpit und Übersicht
          </h3>
          <p>
            Beim Öffnen des Tagebuches sehen Sie immer den aktuellen Kalendertag. Wenn Sie Mahlzeiten und Aktivitäten
            nachtragen möchten, wechseln Sie das Datum mit den Pfeil-Buttons zum gewünschten Tag. Ein Klick auf das
            Datum öffnet zudem einen Kalender, in dem Sie das Datum direkt auswählen können.
          </p>
          <div className="mt-4 mb-4">
            <TourWrapper>
              <JournalHeaderDemo />
            </TourWrapper>
          </div>
          <p>
            Direkt darunter zeigt Ihnen das <b>Cockpit</b> auf einen Blick, wie viele Kalorien Sie am jeweiligen Tag
            bereits gegessen haben, wie viele Kalorien Sie durch Aktivitäten verbraucht haben und wie viele Kalorien
            Ihnen noch verbleiben.
          </p>

          <div className="mt-4">
            <TourWrapper>
              <CockpitDemo />
            </TourWrapper>
          </div>

          <h3>
            <HeadlineAnchor id="eintragen" />
            Einträge hinzufügen
          </h3>
          <p>
            Über den Button <em>Zum Tagebuch hinzufügen</em>
            öffnen Sie ein Fenster mit vier Möglichkeiten: <em>Suche</em>, <em>Favoriten</em>,{' '}
            <em>Mahlzeit direkt eintragen</em> und <em>Aktivität direkt eintragen</em>. So fügen Sie Nahrungsmittel,
            Rezepte oder Aktivitäten einfach und unkompliziert Ihrem Tagebuch hinzu.
          </p>

          <div className="mt-4 mb-4">
            <TourWrapper>
              <JournalActionsDemo />
            </TourWrapper>
          </div>
          <p>
            In der <b>Tagebuch-Übersicht</b> sind Ihre Einträge nach Mahlzeiten und Aktivitäten gruppiert. Über das
            Drei-Punkte-Menü können Sie Einträge editieren, kopieren, verschieben, favorisieren oder entfernen.
            Menüplan-Vorschläge können nicht kopiert oder verschoben werden, dafür steht <em>Als gegessen markieren</em>{' '}
            zur Verfügung.
          </p>
          <div className="mt-4" style={{ paddingBottom: '7rem' }}>
            <TourWrapper markerStyle={{ top: '0', right: '-14px' }}>
              <JournalCardDemo />
            </TourWrapper>
          </div>

          <h3 className="mt-6">
            <HeadlineAnchor id="seitenleiste" />
            Seitenleiste im Tagebuch
          </h3>
          <p>
            In der rechten Spalte des Tagebuchs können Sie unter anderem Ihre <b>Makronährstoffe</b>, die{' '}
            <b>Flüssigkeitszufuhr</b>, den <b>Gemüse- &amp; Früchte-Tracker</b> und Ihre <b>Notizen</b> einsehen. Welche
            Widgets angezeigt werden, legen Sie in den Einstellungen fest.
          </p>
          <div className="mt-4">
            <TourWrapper>
              <SidebarWidgetsDemo />
            </TourWrapper>
          </div>
        </section>

        <section className="content-section">
          <h2>
            <HeadlineAnchor id="menueplan" />
            Ihr individueller Menüplan
          </h2>
          <p>
            Sie ernähren sich vegetarisch, vegan oder omnivor? Aktivieren Sie Ihren individuellen Menüplan unter{' '}
            <em>Einstellungen → Menüplan</em>. Dort wählen Sie auch Ihre Ernährungsform. Wenn der Menüplan aktiv ist,
            zeigen wir Ihnen täglich wechselnde Vorschläge direkt in Ihrem Tagebuch an.
          </p>
          <div className="mt-4 mb-4">
            <TourWrapper markerStyle={{ top: '4px', right: '34px' }}>
              <SettingsSwitchDemo />
            </TourWrapper>
          </div>
          <p>
            Der auf Ihre Ernährungsform angepasste Menüplan ist im Tagebuch auf Kärtchen mit grauem Hintergrund
            dargestellt. Markieren Sie einen Vorschlag mit einem Klick auf das Plus als gegessen, werden die Kalorien in
            Ihrem Tagebuch einkalkuliert.
          </p>
          <div className="mt-4 mb-4">
            <TourWrapper markerStyle={{ top: '68px', right: '45px' }}>
              <MenuPlanCardDemo />
            </TourWrapper>
          </div>
        </section>

        <section className="content-section">
          <h2>
            <HeadlineAnchor id="analyse" />
            Die eBalance-Analyse
          </h2>
          <p>
            Unter dem Menüpunkt <em>Analyse</em> finden Sie Daten zu Ihrer Ernährung (<em>Nährwertdiagramm</em>,{' '}
            <em>Kalorienbilanz</em> und <em>Mahlzeitenverteilung</em>) und Ihren Körpermassen (<em>Masse</em>).
          </p>

          <h3>
            <HeadlineAnchor id="naehrwerte" />
            Nährwertdiagramm
          </h3>
          <p>
            Auf der Seite <em>Nährwertdiagramm</em> vergleichen Sie Ihren Konsum von Makronährstoffen mit den
            eBalance-Empfehlungen. Sie können die Ansicht wechseln: zwischen einem Tag, einer Woche, zwei Wochen, einem
            Monat oder einem benutzerdefinierten Zeitraum.
          </p>

          <h3>
            <HeadlineAnchor id="kalorienbilanz" />
            Kalorienbilanz
          </h3>
          <p>
            Auf der Seite <em>Kalorienbilanz</em> sehen Sie, wie sich Ihre Kalorienaufnahme über die Zeit zu Ihrem Ziel
            verhält. Sie können den dargestellten Zeitraum wechseln: eine Woche, ein Monat, seit Programmstart oder
            einen benutzerdefinierten Zeitraum. Die blaue Linie zeigt Ihr Kalorienziel.
          </p>

          <h3>
            <HeadlineAnchor id="mahlzeitenverteilung" />
            Mahlzeitenverteilung
          </h3>
          <p>
            Auf der Seite <em>Mahlzeitenverteilung</em> sehen Sie, wie sich Ihre Kalorien auf die täglichen Mahlzeiten
            verteilen. Auch diese Ansicht können Sie sich über verschiedene Zeiträume anzeigen lassen, von einer Woche
            bis zu einem Jahr.
          </p>

          <h3>
            <HeadlineAnchor id="masse" />
            Masse
          </h3>
          <p>
            Auf der Seite Masse finden Sie alles zu Ihren Körpermassen. Oben befindet sich der <b>Gewichtstacho</b>, der
            anzeigt, wie viel Sie bereits von Ihrem Ziel geschafft haben.
          </p>
          <TourWrapper>
            <div className="standardcard my-4 mx-6 pt-3 pb-2">
              <GaugeGoal initialWeight={100} currentWeight={97} targetWeight={90} />
            </div>
          </TourWrapper>
          <p>
            Daneben finden Sie die <b>Veränderung Ihres Bauchumfangs</b>, sobald Sie hier genug Werte eingetragen haben.
            Direkt unterhalb finden Sie jeweils die Eingabefelder für Ihr Gewicht und Ihren Bauchumfang.
          </p>
          <p>
            Im nächsten Diagramm sehen Sie den <b>Verlauf Ihres Gewichts</b> – oder, wenn Sie umschalten, den Verlauf
            Ihres BMI. Diese Ansicht können Sie sich auch über verschiedene Laufzeiten anzeigen lassen.
          </p>
          <p>
            Darunter finden Sie Ihr gewohntes <b>Gewichtsprofil</b> und alle Daten noch einmal in einer Tabelle.
          </p>
        </section>

        <section className="content-section">
          <h2>
            <HeadlineAnchor id="inspirieren" />
            Lassen Sie sich inspirieren
          </h2>
          <p>
            Unter dem Menüpunkt <em>Inspiration</em> finden Sie alle eBalance-Rezepte. Mit der Suche und den Filtern –
            etwa nach Ernährungsvariante, Kalorien pro Portion, Zubereitungszeit und Kategorie – können Sie die Auswahl
            noch verfeinern. Zudem finden Sie unter <em>Inspiration</em> Links zum Magazin und den
            eBalance-Botschaftern.
          </p>
        </section>

        <section className="content-section">
          <h2>
            <HeadlineAnchor id="eigene" />
            Eigene Nahrungsmittel und Rezepte erstellen
          </h2>
          <p>
            Sie möchten Ihr Lieblingsrezept speichern oder ein Nahrungsmittel in Ihr Tagebuch eintragen, das Sie nicht
            in unserer umfangreichen Datenbank finden können? Unter dem Menüpunkt <em>Mein Essen</em> verwalten Sie Ihre
            eigenen Nahrungsmittel und Rezepte. Über <em>Zum Tagebuch hinzufügen</em> im Tagebuch können Sie diese
            anschliessend verwenden. Ihre Favoriten verwalten Sie auf der Seite <em>Favoriten</em>.
          </p>
        </section>

        <section className="content-section">
          <h2>
            <HeadlineAnchor id="einstellungen" />
            Einstellungen und persönliche Daten
          </h2>
          <p>
            Über das Benutzermenü (<em>Hallo, ...!</em>) gelangen Sie zu <em>Meine Kundendaten</em> sowie zu den{' '}
            <em>Einstellungen</em>. Dort können Sie unter anderem festlegen, welche Widgets im Tagebuch angezeigt
            werden, Ihren Menüplan konfigurieren, die Standard-Trinkglasgrösse festlegen, Ihr Profilbild hochladen und
            Ihre Basisdaten wie Geburtsdatum und Körpergrösse ändern.
          </p>
          <p>
            Unter <em>Mein Programm</em> in den Einstellungen können Sie einen Neustart des Abnehmprogramms durchführen.
            Das ist zum Beispiel dann sinnvoll, wenn Sie nach erfolgreicher Gewichtsreduktion vom Programm{' '}
            <em>Gewicht abnehmen</em> auf das Programm <em>Gewicht halten</em> wechseln möchten.
          </p>
        </section>

        <section className="content-section">
          <h2>
            <HeadlineAnchor id="faq" />
            Häufige Fragen (FAQ)
          </h2>
          <p>
            Auf unserer Fragen-und-Antworten-Seite finden Sie die häufigsten Anliegen unserer Mitglieder.{' '}
            <a href="https://ebalance.ch/hilfe-portal?utm_source=Portal&utm_medium=referral&utm_campaign=zurfaqseite">
              Hier
            </a>{' '}
            geht es zur Seite.
          </p>
        </section>

        <section className="content-section">
          <h2>
            <HeadlineAnchor id="feedback" />
            Feedback
          </h2>
          <p>
            Sie haben Fragen oder Anregungen zur Plattform? Sie würden etwas anders machen, oder es fehlt Ihnen etwas
            auf den aktuellen Seiten? Dann freuen wir uns sehr auf Ihr Feedback per E-Mail an{' '}
            <a href="mailto:info@eBalance.ch">info@eBalance.ch</a>. Weitere Möglichkeiten, uns zu erreichen, finden Sie
            auf unserer&nbsp;
            <a href="https://ebalance.ch/kontakt-2" target="_blank" rel="noopener noreferrer">
              Kontaktseite
            </a>
            .
          </p>
        </section>
      </div>
    </PageLayout>
  )
}

export default HelpAndTourPage
